<template>
  <div class="chat-window card shadow mb-0">
    <!-- Header -->
    <div class="card-header p-2 d-flex justify-content-between align-items-center" @click="toggleMinimize" style="cursor: pointer;">
      <div class="d-flex align-items-center">
        <!-- Profile picture -->
        <img v-if="chat.contact_picture" :src="chat.contact_picture" class="rounded-circle me-2" style="width: 24px; height: 24px; object-fit: cover;" alt="user">
        <i v-else :class="[chat.platform === 'instagram' ? 'bx bxl-instagram text-danger' : 'bx bxl-messenger text-primary', 'me-2 font-size-18']"></i>
        <h6 class="m-0 chat-name d-flex align-items-center">
          <i v-if="chat.contact_picture" :class="chat.platform === 'instagram' ? 'bx bxl-instagram text-danger me-1' : 'bx bxl-messenger text-primary me-1'"></i>
          {{ chat.name || chat.recipient_id }}
        </h6>
      </div>
      <div class="d-flex align-items-center">
        <!-- Meta 24hr policy warning dot if needed -->
        <span v-if="!canReply" class="badge badge-soft-danger me-2" title="Beyond 24hr reply window">24h Limit</span>
        <button class="btn btn-sm btn-ghost-secondary p-0 me-2" @click.stop="toggleMinimize">
          <i :class="isMinimized ? 'bx bx-chevron-up' : 'bx bx-chevron-down'"></i>
        </button>
        <button class="btn btn-sm btn-ghost-secondary p-0" @click.stop="$emit('close')">
          <i class="bx bx-x fs-5"></i>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div v-show="!isMinimized" class="card-body chat-body p-3 overflow-auto" ref="chatBody">
      <div v-if="chat.messages.length === 0" class="text-center my-3 text-muted">
        <small>No messages yet.</small>
      </div>
      
      <div 
        v-for="msg in chat.messages" 
        :key="msg.messageSID"
        :class="['d-flex mb-3', msg.isClient === 0 ? 'justify-content-end' : 'justify-content-start']"
      >
        <div :class="['message-bubble p-2 rounded-3', msg.isClient === 0 ? 'bg-primary text-white' : 'bg-light']">
          <p class="m-0 text-break" style="white-space: pre-wrap;">{{ msg.message }}</p>
          <div v-if="msg.image">
            <img :src="msg.image" alt="attachment" class="img-fluid rounded mt-2" style="max-width: 150px; cursor: pointer;" />
          </div>
          <small :class="['d-block mt-1', msg.isClient === 0 ? 'text-white-50' : 'text-muted']" style="font-size: 0.65rem;">
            {{ formatTime(msg.createdOn) }}
          </small>
        </div>
      </div>
    </div>

    <!-- Footer Input -->
    <div v-show="!isMinimized" class="card-footer p-2">
      <form @submit.prevent="sendMessage" class="d-flex align-items-center">
        <input 
          type="text" 
          v-model="newMessage" 
          class="form-control form-control-sm border-0 bg-light rounded-pill" 
          placeholder="Type a message..." 
          :disabled="isSending"
          ref="inputField"
          style="padding: 8px 15px;"
        />
        <button 
          type="submit" 
          class="btn btn-sm btn-ghost-primary rounded-circle ms-2 flex-shrink-0 d-flex align-items-center justify-content-center" 
          :disabled="isSending || !newMessage.trim()"
          style="width: 32px; height: 32px; padding: 0;"
        >
          <i class="bx bxs-send font-size-20"></i>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: "ChatWindow",
  props: {
    chat: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isMinimized: false,
      newMessage: "",
      isSending: false
    };
  },
  computed: {
    canReply() {
      // Basic 24h Meta policy check
      if (this.chat.messages.length === 0) return true;
      // Get last message from client
      const clientMessages = this.chat.messages.filter(m => m.isClient === 1);
      if (clientMessages.length === 0) return true; // Might be a lead we're contacting first
      
      const lastMsg = clientMessages[clientMessages.length - 1];
      const msgDate = new Date(lastMsg.createdOn);
      const now = new Date();
      const diffHours = (now - msgDate) / (1000 * 60 * 60);
      return diffHours <= 24;
    }
  },
  watch: {
    'chat.messages': {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true
    }
  },
  mounted() {
    this.scrollToBottom();
  },
  methods: {
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
      if (!this.isMinimized) {
        this.$nextTick(() => {
          this.scrollToBottom();
          if (this.$refs.inputField) {
            this.$refs.inputField.focus();
          }
        });
      }
    },
    scrollToBottom() {
      const body = this.$refs.chatBody;
      if (body) {
        body.scrollTop = body.scrollHeight;
      }
    },
    formatTime(dateString) {
      if (!dateString) return "";
      const d = new Date(dateString);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    async sendMessage() {
      const text = this.newMessage.trim();
      if (!text) return;

      this.isSending = true;
      try {
        await this.$store.dispatch('metaChat/sendMessage', {
          recipient_id: this.chat.recipient_id,
          page_id: this.chat.page_id,
          message: text
        });
        
        this.newMessage = "";
        // Optional: fetch messages immediately
        await this.$store.dispatch('metaChat/fetchMessages', {
          recipient_id: this.chat.recipient_id,
          page_id: this.chat.page_id
        });
        
        this.$nextTick(() => {
          this.scrollToBottom();
          if (this.$refs.inputField) {
            this.$refs.inputField.focus();
          }
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.isSending = false;
      }
    }
  }
};
</script>

<style scoped>
.chat-window {
  width: 330px;
  max-width: 100vw;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: height 0.3s ease;
  box-shadow: 0 -5px 15px rgba(0, 0, 0, 0.1) !important;
  pointer-events: auto; /* Ensure clicks work inside the window */
}

.chat-header {
  cursor: pointer;
  height: 40px;
}

.chat-name {
  font-size: 0.9rem;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-body {
  height: 320px;
  overflow-y: auto;
}

.chat-footer {
  height: 50px;
}

.message-bubble {
  max-width: 85%;
  font-size: 0.85rem;
  line-height: 1.3;
}
</style>
